/* ================================================================
   LFJ THEATER PLAYER — shared by JTV and the LFJ room (CP-THEATER,
   simplified Checkpoint 59)
   ----------------------------------------------------------------
   Two source types per entry, handled very differently on purpose:

     WEBSITE VERSION — a real uploaded file (Shopify CDN mp4), played
       through a native <video> element with the site's own FULL
       custom control bar: play/pause, scrub bar with buffered range,
       time, volume, mute, playback speed, skip ±10s, fullscreen,
       picture-in-picture, keyboard shortcuts. This is "the fixed site
       video player" and is the priority path whenever a website
       source exists for an entry.

     YOUTUBE VERSION — a plain YouTube iframe embed with YouTube's own
       native controls. NOT driven by the custom control bar — trying
       to fully replicate play/scrub/volume/speed against YouTube's
       API is fragile in practice (state-sync gaps, API load races),
       and isn't the goal here: YouTube sources get the themed
       background/frame, native YouTube chrome underneath, nothing
       more. Matches exactly how lfj-archive.js has always embedded
       YouTube. The control bar is hidden entirely while this source
       is showing.

   SOURCE RESOLUTION per entry (from videos.json):
     entry.websiteVideoUrl  — a real CDN mp4 URL = the WEBSITE version
     entry.youtubeId        — a YouTube video ID = the YOUTUBE version
   If both exist, a WEBSITE / YOUTUBE toggle pill renders in the
   player chrome and WEBSITE plays first by default. If only one
   exists, no toggle renders — that source just plays.

   USAGE:
     LFJ_THEATER_PLAYER.mount(stageEl, {
       title: 'Episode title',
       websiteVideoUrl: 'https://cdn.shopify.com/.../ep.mp4' | null,
       youtubeId: 'abc123' | null
     });
     LFJ_THEATER_PLAYER.unmount(stageEl);

   Engine pattern: safeRun wrapper, no init-guard needed (this is a
   mount/unmount API, not a page-load listener) — matches the rest
   of the theme's defensive style.
================================================================ */
(function () {
  if (window.LFJ_THEATER_PLAYER) return;

  function safeRun(label, fn) {
    try { return fn(); }
    catch (e) { console.warn('[LFJ:theater]', label, e); }
  }

  function _fmtTime(sec) {
    if (!isFinite(sec) || sec < 0) sec = 0;
    var m = Math.floor(sec / 60);
    var s = Math.floor(sec % 60);
    return m + ':' + (s < 10 ? '0' : '') + s;
  }

  /* Clean an Instagram post/reel URL to its public embed form — same
     pattern lfj-archive.js already uses. IG's embed carries its own
     playback UI, so this path never gets our custom control bar. */
  function _igEmbedSrc(url) {
    if (!url) return null;
    var clean = String(url).split('?')[0];
    if (!clean.endsWith('/')) clean += '/';
    return clean + 'embed/?utm_source=ig_embed';
  }

  /* ════════════════════════════════════════════════════════════
     ADAPTER: NATIVE <video> — the "website version"
  ════════════════════════════════════════════════════════════ */
  function NativeAdapter(container, src) {
    var video = document.createElement('video');
    video.className = 'lfj-theater-native-video';
    video.src = src;
    video.playsInline = true;
    video.preload = 'metadata';
    container.appendChild(video);

    var listeners = {};
    function emit(name, val) { (listeners[name] || []).forEach(function(fn){ try{fn(val);}catch(e){} }); }

    video.addEventListener('timeupdate', function () { emit('timeupdate', video.currentTime); });
    video.addEventListener('play',  function () { emit('play'); });
    video.addEventListener('pause', function () { emit('pause'); });
    video.addEventListener('ended', function () { emit('ended'); });
    video.addEventListener('volumechange', function () { emit('volumechange', { volume: video.volume, muted: video.muted }); });
    video.addEventListener('loadedmetadata', function () { emit('loadedmetadata', video.duration); });
    video.addEventListener('waiting', function () { emit('waiting'); });
    video.addEventListener('playing', function () { emit('playing'); });

    video.play().catch(function(){ /* autoplay may be blocked — user presses play */ });

    return {
      el: video,
      kind: 'website',
      supportsPiP: !!(document.pictureInPictureEnabled && video.requestPictureInPicture),
      on: function (name, fn) { (listeners[name] = listeners[name] || []).push(fn); },
      play:  function () { return video.play(); },
      pause: function () { video.pause(); },
      seek:  function (t) { video.currentTime = t; },
      setVolume: function (v) { video.volume = Math.max(0, Math.min(1, v)); },
      setMuted:  function (b) { video.muted = b; },
      setRate:   function (r) { video.playbackRate = r; },
      getCurrentTime: function () { return video.currentTime || 0; },
      getDuration:    function () { return video.duration || 0; },
      getBuffered: function () {
        try {
          if (video.buffered.length && video.duration) {
            return video.buffered.end(video.buffered.length - 1) / video.duration;
          }
        } catch(e) {}
        return 0;
      },
      isPaused: function () { return video.paused; },
      requestFullscreen: function () {
        var el = container.querySelector('.lfj-theater-native-video') || container;
        if (el.requestFullscreen) el.requestFullscreen();
        else if (video.webkitEnterFullscreen) video.webkitEnterFullscreen();
      },
      requestPiP: function () {
        if (document.pictureInPictureEnabled && video.requestPictureInPicture) {
          video.requestPictureInPicture().catch(function(){});
        }
      },
      destroy: function () {
        try { video.pause(); video.src = ''; video.load(); } catch(e){}
        if (video.parentNode) video.parentNode.removeChild(video);
      }
    };
  }

  /* ════════════════════════════════════════════════════════════
     YOUTUBE — plain native embed, NOT a driven adapter (Checkpoint 59)
     ----------------------------------------------------------------
     Previously this drove a full custom control bar over the YouTube
     IFrame API via lfj-yt-mask.js — real controls, but fragile in
     practice (API load races, state-sync gaps, the mask's endscreen
     overlay). Per direct instruction: YouTube sources get the themed
     background/frame, not an attempt at full custom controls, since
     you can't reliably drive that against YouTube's API. This now
     matches exactly how lfj-archive.js has always embedded YouTube —
     a plain iframe with YouTube's own native controls. No adapter
     object, nothing for the control bar to bind to; the control bar
     is hidden entirely while a YouTube source is showing.
  ════════════════════════════════════════════════════════════ */
  function mountYouTubeEmbed(container, videoId, title) {
    var frame = document.createElement('iframe');
    frame.className = 'lfj-theater-yt-frame';
    frame.src = 'https://www.youtube.com/embed/' + videoId + '?autoplay=1&rel=0&modestbranding=1&playsinline=1';
    frame.setAttribute('frameborder', '0');
    frame.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture');
    frame.setAttribute('allowfullscreen', '');
    frame.title = title || 'JTV episode';
    container.appendChild(frame);
  }

  /* ════════════════════════════════════════════════════════════
     PIXEL ICONS FOR THE CONTROL BAR (Checkpoint 61 / 4h)
     The bar used raw Unicode glyphs (▶ ❚❚ ↺ ▦ ⛶). Many platforms
     render some of those as full-color emoji or fall back to a
     different font, which broke the monochrome pixel look (the volume
     icon had the same problem and was already fixed in CP59). These
     are 8x8 pixel grids drawn as currentColor SVG rects, same
     technique as lfj-lfj.js's PIXEL_ICONS and the menu icons, so the
     existing per-room accent color still drives them.
  ════════════════════════════════════════════════════════════ */
  var CTRL_ICONS = {
    play:  ['#.......', '###.....', '#####...', '#######.', '#######.', '#####...', '###.....', '#.......'],
    pause: ['.##..##.', '.##..##.', '.##..##.', '.##..##.', '.##..##.', '.##..##.', '.##..##.', '.##..##.'],
    back:  ['...#...#', '..##..##', '.###.###', '########', '########', '.###.###', '..##..##', '...#...#'],
    fwd:   ['#...#...', '##..##..', '###.###.', '########', '########', '###.###.', '##..##..', '#...#...'],
    pip:   ['########', '#......#', '#......#', '#..#####', '#..#...#', '#..#...#', '#..#####', '########'],
    full:  ['###..###', '#......#', '#......#', '........', '........', '#......#', '#......#', '###..###']
  };
  function _pxIcon(name) {
    var rows = CTRL_ICONS[name];
    if (!rows) return '';
    var cells = '';
    for (var y = 0; y < rows.length; y++) {
      for (var x = 0; x < rows[y].length; x++) {
        if (rows[y].charAt(x) === '#') cells += '<rect x="' + x + '" y="' + y + '" width="1" height="1"/>';
      }
    }
    return '<svg class="lfj-tc-px" viewBox="0 0 8 8" aria-hidden="true" focusable="false"'
      + ' fill="currentColor" shape-rendering="crispEdges">' + cells + '</svg>';
  }

  /* ════════════════════════════════════════════════════════════
     CUSTOM CONTROL BAR — built once, driven by whichever adapter
  ════════════════════════════════════════════════════════════ */
  function buildControlBar(root, videoStage, getAdapter, opts) {
    var bar = document.createElement('div');
    bar.className = 'lfj-theater-controls';
    bar.innerHTML =
      '<button type="button" class="lfj-tc-btn lfj-tc-play" aria-label="Play/Pause">' +
        '<span class="lfj-tc-icon-play">' + _pxIcon('play') + '</span>' +
        '<span class="lfj-tc-icon-pause" hidden>' + _pxIcon('pause') + '</span>' +
      '</button>' +
      '<button type="button" class="lfj-tc-btn lfj-tc-skipback" aria-label="Back 10 seconds">' + _pxIcon('back') + '<span class="lfj-tc-skip-n">10</span></button>' +
      '<span class="lfj-tc-time lfj-tc-time-current">0:00</span>' +
      '<div class="lfj-tc-scrub" role="slider" aria-label="Seek" tabindex="0">' +
        '<div class="lfj-tc-scrub-track">' +
          '<div class="lfj-tc-scrub-buffered"></div>' +
          '<div class="lfj-tc-scrub-played"></div>' +
          '<div class="lfj-tc-scrub-thumb"></div>' +
        '</div>' +
      '</div>' +
      '<span class="lfj-tc-time lfj-tc-time-duration">0:00</span>' +
      '<button type="button" class="lfj-tc-btn lfj-tc-skipfwd" aria-label="Forward 10 seconds"><span class="lfj-tc-skip-n">10</span>' + _pxIcon('fwd') + '</button>' +
      '<div class="lfj-tc-vol-group">' +
        '<button type="button" class="lfj-tc-btn lfj-tc-mute" aria-label="Mute">' +
          '<span class="lfj-tc-vol-icon"></span>' +
        '</button>' +
        '<input type="range" class="lfj-tc-vol-slider" min="0" max="1" step="0.05" value="1" aria-label="Volume">' +
      '</div>' +
      '<div class="lfj-tc-speed-group">' +
        '<button type="button" class="lfj-tc-btn lfj-tc-speed" aria-label="Playback speed">1x</button>' +
        '<div class="lfj-tc-speed-menu" hidden>' +
          '<button type="button" data-rate="0.5">0.5x</button>' +
          '<button type="button" data-rate="0.75">0.75x</button>' +
          '<button type="button" data-rate="1" class="lfj-tc-speed-active">1x</button>' +
          '<button type="button" data-rate="1.25">1.25x</button>' +
          '<button type="button" data-rate="1.5">1.5x</button>' +
          '<button type="button" data-rate="2">2x</button>' +
        '</div>' +
      '</div>' +
      '<button type="button" class="lfj-tc-btn lfj-tc-pip" aria-label="Picture in picture">' + _pxIcon('pip') + '</button>' +
      '<button type="button" class="lfj-tc-btn lfj-tc-fullscreen" aria-label="Fullscreen">' + _pxIcon('full') + '</button>';
    root.appendChild(bar);

    var els = {
      play:        bar.querySelector('.lfj-tc-play'),
      iconPlay:    bar.querySelector('.lfj-tc-icon-play'),
      iconPause:   bar.querySelector('.lfj-tc-icon-pause'),
      skipBack:    bar.querySelector('.lfj-tc-skipback'),
      skipFwd:     bar.querySelector('.lfj-tc-skipfwd'),
      timeCur:     bar.querySelector('.lfj-tc-time-current'),
      timeDur:     bar.querySelector('.lfj-tc-time-duration'),
      scrub:       bar.querySelector('.lfj-tc-scrub'),
      scrubPlayed: bar.querySelector('.lfj-tc-scrub-played'),
      scrubBuf:    bar.querySelector('.lfj-tc-scrub-buffered'),
      scrubThumb:  bar.querySelector('.lfj-tc-scrub-thumb'),
      mute:        bar.querySelector('.lfj-tc-mute'),
      volSlider:   bar.querySelector('.lfj-tc-vol-slider'),
      speedBtn:    bar.querySelector('.lfj-tc-speed'),
      speedMenu:   bar.querySelector('.lfj-tc-speed-menu'),
      pip:         bar.querySelector('.lfj-tc-pip'),
      fullscreen:  bar.querySelector('.lfj-tc-fullscreen')
    };

    var isDraggingScrub = false;
    var wasMutedBeforeDrag = false;

    function refreshPlayIcon() {
      var a = getAdapter();
      if (!a) return;
      var paused = a.isPaused();
      els.iconPlay.hidden = !paused;
      els.iconPause.hidden = paused;
      videoStage.classList.toggle('lfj-theater-stage--playing', !paused);
    }

    els.play.addEventListener('click', function () {
      var a = getAdapter(); if (!a) return;
      if (a.isPaused()) a.play(); else a.pause();
      setTimeout(refreshPlayIcon, 50);
    });
    videoStage.addEventListener('click', function (e) {
      if (e.target === videoStage) els.play.click();
    });

    els.skipBack.addEventListener('click', function () {
      var a = getAdapter(); if (!a) return;
      a.seek(Math.max(0, a.getCurrentTime() - 10));
    });
    els.skipFwd.addEventListener('click', function () {
      var a = getAdapter(); if (!a) return;
      a.seek(Math.min(a.getDuration(), a.getCurrentTime() + 10));
    });

    function updateScrubFromEvent(e) {
      var rect = els.scrub.getBoundingClientRect();
      var clientX = (e.touches && e.touches[0] ? e.touches[0].clientX : e.clientX);
      var pct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      var a = getAdapter(); if (!a) return pct;
      a.seek(pct * a.getDuration());
      return pct;
    }
    els.scrub.addEventListener('mousedown', function (e) { isDraggingScrub = true; updateScrubFromEvent(e); });
    els.scrub.addEventListener('touchstart', function (e) { isDraggingScrub = true; updateScrubFromEvent(e); }, {passive:true});
    document.addEventListener('mousemove', function (e) { if (isDraggingScrub) updateScrubFromEvent(e); });
    document.addEventListener('touchmove', function (e) { if (isDraggingScrub) updateScrubFromEvent(e); }, {passive:true});
    document.addEventListener('mouseup', function () { isDraggingScrub = false; });
    document.addEventListener('touchend', function () { isDraggingScrub = false; });
    els.scrub.addEventListener('keydown', function (e) {
      var a = getAdapter(); if (!a) return;
      if (e.key === 'ArrowRight') a.seek(Math.min(a.getDuration(), a.getCurrentTime() + 5));
      if (e.key === 'ArrowLeft')  a.seek(Math.max(0, a.getCurrentTime() - 5));
    });

    els.mute.addEventListener('click', function () {
      var a = getAdapter(); if (!a) return;
      var willMute = !els.mute.classList.contains('is-muted');
      a.setMuted(willMute);
      els.mute.classList.toggle('is-muted', willMute);
    });
    els.volSlider.addEventListener('input', function () {
      var a = getAdapter(); if (!a) return;
      var v = parseFloat(els.volSlider.value);
      a.setVolume(v);
      var muted = v === 0;
      a.setMuted(muted);
      els.mute.classList.toggle('is-muted', muted);
    });

    els.speedBtn.addEventListener('click', function () {
      els.speedMenu.hidden = !els.speedMenu.hidden;
    });
    els.speedMenu.querySelectorAll('button').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var a = getAdapter(); if (!a) return;
        var rate = parseFloat(btn.dataset.rate);
        a.setRate(rate);
        els.speedBtn.textContent = rate + 'x';
        els.speedMenu.querySelectorAll('button').forEach(function(b){ b.classList.remove('lfj-tc-speed-active'); });
        btn.classList.add('lfj-tc-speed-active');
        els.speedMenu.hidden = true;
      });
    });
    document.addEventListener('click', function (e) {
      if (!bar.contains(e.target)) els.speedMenu.hidden = true;
    });

    els.pip.addEventListener('click', function () {
      var a = getAdapter(); if (!a) return;
      a.requestPiP();
    });
    els.fullscreen.addEventListener('click', function () {
      var a = getAdapter(); if (!a) return;
      a.requestFullscreen();
    });

    /* Keyboard shortcuts — active while this stage is in view */
    function onKeydown(e) {
      var a = getAdapter(); if (!a) return;
      if (e.target && /input|textarea/i.test(e.target.tagName)) return;
      if (e.key === ' ') { e.preventDefault(); els.play.click(); }
      if (e.key === 'ArrowRight') a.seek(Math.min(a.getDuration(), a.getCurrentTime() + 10));
      if (e.key === 'ArrowLeft')  a.seek(Math.max(0, a.getCurrentTime() - 10));
      if (e.key === 'ArrowUp')   { e.preventDefault(); els.volSlider.value = Math.min(1, parseFloat(els.volSlider.value)+0.1); els.volSlider.dispatchEvent(new Event('input')); }
      if (e.key === 'ArrowDown') { e.preventDefault(); els.volSlider.value = Math.max(0, parseFloat(els.volSlider.value)-0.1); els.volSlider.dispatchEvent(new Event('input')); }
      if (e.key === 'f' || e.key === 'F') a.requestFullscreen();
      if (e.key === 'm' || e.key === 'M') els.mute.click();
    }
    document.addEventListener('keydown', onKeydown);

    /* ── Adapter-driven state sync ────────────────────────────── */
    function bindAdapter(a) {
      if (!a) return;
      els.pip.hidden = !a.supportsPiP;

      a.on('play',  refreshPlayIcon);
      a.on('pause', refreshPlayIcon);
      a.on('ended', function () { refreshPlayIcon(); });
      a.on('loadedmetadata', function (dur) {
        els.timeDur.textContent = _fmtTime(a.getDuration());
      });
      a.on('timeupdate', function (t) {
        if (isDraggingScrub) return;
        var dur = a.getDuration();
        els.timeCur.textContent = _fmtTime(t);
        if (dur > 0) {
          els.timeDur.textContent = _fmtTime(dur);
          var pct = Math.max(0, Math.min(1, t / dur));
          els.scrubPlayed.style.width = (pct*100) + '%';
          els.scrubThumb.style.left = (pct*100) + '%';
        }
        var buf = a.getBuffered();
        els.scrubBuf.style.width = (buf*100) + '%';
      });
      refreshPlayIcon();
    }

    return {
      bindAdapter: bindAdapter,
      show: function () { bar.hidden = false; },
      hide: function () { bar.hidden = true; },
      destroy: function () {
        document.removeEventListener('keydown', onKeydown);
        if (bar.parentNode) bar.parentNode.removeChild(bar);
      }
    };
  }

  /* ════════════════════════════════════════════════════════════
     WEBSITE / YOUTUBE SOURCE TOGGLE
  ════════════════════════════════════════════════════════════ */
  function buildToggle(root, onSwitch) {
    var pill = document.createElement('div');
    pill.className = 'lfj-theater-toggle';
    pill.innerHTML =
      '<button type="button" class="lfj-theater-toggle-opt lfj-theater-toggle-opt--active" data-src="website">WEBSITE</button>' +
      '<button type="button" class="lfj-theater-toggle-opt" data-src="youtube">YOUTUBE</button>';
    root.appendChild(pill);
    pill.querySelectorAll('button').forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (btn.classList.contains('lfj-theater-toggle-opt--active')) return;
        pill.querySelectorAll('button').forEach(function(b){ b.classList.remove('lfj-theater-toggle-opt--active'); });
        btn.classList.add('lfj-theater-toggle-opt--active');
        onSwitch(btn.dataset.src);
      });
    });
    return pill;
  }

  /* ════════════════════════════════════════════════════════════
     PUBLIC API
  ════════════════════════════════════════════════════════════ */
  var MOUNTS = new WeakMap();

  function mount(stageRoot, entry) {
    entry = entry || {};
    unmount(stageRoot);

    var videoStage = document.createElement('div');
    videoStage.className = 'lfj-theater-stage-video';
    stageRoot.appendChild(videoStage);

    var chromeRow = document.createElement('div');
    chromeRow.className = 'lfj-theater-chrome-row';
    stageRoot.appendChild(chromeRow);

    var hasWebsite = !!entry.websiteVideoUrl;
    var hasYouTube = !!entry.youtubeId;
    var hasInstagram = !hasWebsite && !hasYouTube && !!entry.igUrl;
    var currentAdapter = null;
    var controlBar = null;
    var toggle = null;

    /* Instagram-only episodes: IG's embed carries its own playback
       UI, so this path skips the custom control bar and toggle
       entirely — same reasoning Archive already uses for IG embeds. */
    if (hasInstagram) {
      var igSrc = _igEmbedSrc(entry.igUrl);
      if (igSrc) {
        var igFrame = document.createElement('iframe');
        igFrame.className = 'jtv-theater-ig-frame';
        igFrame.src = igSrc;
        igFrame.setAttribute('frameborder', '0');
        igFrame.setAttribute('scrolling', 'no');
        igFrame.setAttribute('allowtransparency', 'true');
        igFrame.title = entry.title || 'JTV episode (Instagram)';
        videoStage.appendChild(igFrame);
      }
      MOUNTS.set(stageRoot, { destroy: function () {} });
      return;
    }

    function loadSource(kind) {
      videoStage.innerHTML = '';
      if (currentAdapter) { safeRun('destroy-adapter', function(){ currentAdapter.destroy(); }); currentAdapter = null; }

      if (kind === 'website' && hasWebsite) {
        currentAdapter = NativeAdapter(videoStage, entry.websiteVideoUrl);
        if (controlBar) { controlBar.show(); controlBar.bindAdapter(currentAdapter); }
      } else if (kind === 'youtube' && hasYouTube) {
        mountYouTubeEmbed(videoStage, entry.youtubeId, entry.title);
        if (controlBar) controlBar.hide();
      }
    }

    /* Toggle only renders when BOTH sources exist. Website wins by
       default per spec ("website version is prioritized first"). */
    if (hasWebsite && hasYouTube) {
      toggle = buildToggle(chromeRow, function (kind) { loadSource(kind); });
    }

    controlBar = buildControlBar(chromeRow, videoStage, function () { return currentAdapter; }, {});

    var initialKind = hasWebsite ? 'website' : (hasYouTube ? 'youtube' : null);
    if (initialKind) {
      loadSource(initialKind);
    } else {
      var empty = document.createElement('div');
      empty.className = 'lfj-theater-empty-src';
      empty.textContent = (window.LF_CONFIG && window.LF_CONFIG.copy && window.LF_CONFIG.copy.jtvNoSignal) || 'NO SIGNAL';
      videoStage.appendChild(empty);
    }

    MOUNTS.set(stageRoot, {
      destroy: function () {
        if (currentAdapter) safeRun('destroy', function(){ currentAdapter.destroy(); });
        if (controlBar) controlBar.destroy();
      }
    });
  }

  function unmount(stageRoot) {
    if (!stageRoot) return;
    var rec = MOUNTS.get(stageRoot);
    if (rec) { rec.destroy(); MOUNTS.delete(stageRoot); }
    stageRoot.innerHTML = '';
  }

  window.LFJ_THEATER_PLAYER = { mount: mount, unmount: unmount };
}());
